import React, { useEffect, useRef } from "react";
import { drawPowerUpGlyph, drawPowerUpToken, getPowerUpVariant } from "../game-canvas/powerup-designs";
import type { PowerUpColor } from "./powerups";
import { asset } from "./portal-env";

// A power-up glyph for the setup/lobby selectors: big and tinted to its affect colour
// (green buff / red debuff / blue all), no circle - the selector button already carries
// the power-up styling. `dim` greys it out for a disabled toggle.
const AFFECTS: Record<PowerUpColor, string> = { green: "self", red: "others", blue: "all" };

// Load each glyph straight from its svg path (keyed by the same basename the selectors
// already use), cached across icons. Avoids depending on the renderer's loader keys,
// which differ from the svg basenames (e.g. quick_turn vs quickturn).
const iconCache = new Map<string, HTMLImageElement>();
function loadIcon(icon: string): HTMLImageElement {
  const src = asset(`/svg/powerup/${icon}.svg`);
  let img = iconCache.get(src);
  if (!img) { img = new Image(); img.src = src; iconCache.set(src, img); }
  return img;
}

interface Props {
  icon: string; // svg basename under /svg/powerup
  color: PowerUpColor;
  size?: number;
  dim?: boolean;
  token?: boolean; // the whole token as the arena shows it (its red / green / blue coin), not the glyph alone
}

export default function PowerUpTokenIcon({ icon, color, size = 26, dim = false, token = false }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = size * dpr;
    c.height = size * dpr;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const img = loadIcon(icon);
    let raf = 0, tries = 0;
    const draw = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size, size);
      if (token) drawPowerUpToken(ctx, size / 2, size / 2, size / 2 - 1, img, AFFECTS[color], getPowerUpVariant(), 0);
      else drawPowerUpGlyph(ctx, size / 2, size / 2, size / 2, img, AFFECTS[color]);
      // The svg may still be decoding; redraw until the glyph lands (capped ~2s).
      if (!(img.complete && img.naturalWidth > 0) && tries++ < 120) raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [icon, color, size, token]);

  return (
    <canvas
      ref={ref}
      width={size}
      height={size}
      style={{ width: size, height: size, display: "block", flex: "0 0 auto", filter: dim ? "grayscale(1)" : "none" }}
    />
  );
}
